import React, { useCallback, useEffect, useState } from 'react';
import { Eye, Loader2, RefreshCw, Send } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { errMsg } from '@/services/staffAttendanceApi';
import { holdsApi } from '@/services/ewayBillApi';
import { fmtDT } from '@/components/ewayBill/shared';
import { slug } from '@/components/ewayBill/TallyNames';
import { AutoPushSummary } from '@/components/ewayBill/AutoPushSummary';
import { DocUploadButton } from '@/components/ewayBill/DocUploadButton';
import { HoldBranchPick } from '@/components/ewayBill/HoldBranchPick';
import { HoldKeyEwb } from '@/components/ewayBill/HoldKeyEwb';

const HEADS = ['Document', 'DC / Challan', 'Customer / EBO', 'Uploaded', 'Remarks', ''];
const KIND = { customer_invoice: 'Invoice', transfer_out: 'Transfer Out', outside_invoice: 'Invoice · not in WMS' };
const hkey = (h) => `${h.doc_type}:${h.doc_no}`;
const resultToast = (r) => {
  if (r.result === 'sent') toast.success(`${r.doc_no}: e-way bill made (${r.status === 'AWAITING' ? 'ready for the portal JSON' : 'sent to Tally'})`);
  else if (r.result === 'skipped') toast.info(`${r.doc_no}: ${r.note}`);
  else toast.warning(`${r.doc_no} still needs attention: ${(r.reasons || [])[0] || ''}`, { duration: 7000 });
};

const HoldRow = ({ h, busy, km, setKm, onRecheck, onPick, onOpen, onKeyed }) => {
  const id = slug(h.doc_no);
  return (
    <TableRow data-testid={`hold-row-${id}`}>
      <TableCell className="font-mono text-xs font-semibold whitespace-nowrap">{h.doc_no}
        <div className="font-sans font-normal text-gray-500"><span className="mr-1 rounded bg-slate-100 px-1 text-[10px] text-slate-700" data-testid={`hold-kind-${id}`}>{KIND[h.doc_type] || 'Invoice'}</span>{h.invoice_date}</div></TableCell>
      <TableCell className="max-w-[150px] font-mono text-xs" data-testid={`hold-dc-${id}`}>
        {(h.dc_number || '—').split(/,\s*/).map((dc) => <div key={dc} className="whitespace-nowrap">{dc}</div>)}
      </TableCell>
      <TableCell className="text-sm">
        <div className="max-w-[260px] truncate">{h.customer || '—'}</div>
        {h.customer_choices?.length > 1 && (
          <HoldBranchPick choices={h.customer_choices} value={h.customer_id} disabled={busy} onPick={onPick} testId={`hold-branch-${id}`} />
        )}
      </TableCell>
      <TableCell className="text-xs whitespace-nowrap">{fmtDT(h.uploaded_at || h.first_held_at)}<div className="text-gray-500">{h.uploaded_by_name}</div></TableCell>
      <TableCell className="min-w-[280px]">
        <ul className="list-disc pl-4 text-xs text-amber-900 space-y-0.5" data-testid={`hold-reasons-${id}`}>{h.reasons.map((r) => <li key={r}>{r}</li>)}</ul>
      </TableCell>
      <TableCell className="min-w-[180px]">
        <div className="flex flex-wrap items-center justify-end gap-1 whitespace-nowrap">
          {h.needs_distance && (
            <Input value={km} onChange={(e) => setKm(e.target.value.replace(/\D/g, ''))} placeholder="km" inputMode="numeric"
              className="h-8 w-20 text-sm" title={`Road distance PIN ${h.from_pin} → ${h.to_pin}`} data-testid={`hold-km-${id}`} />
          )}
          <Button size="sm" variant="outline" className="h-8" onClick={onRecheck} disabled={busy || (h.needs_distance && !km)} data-testid={`hold-recheck-${id}`}>
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}Re-check &amp; push</Button>
          <HoldKeyEwb h={h} id={id} disabled={busy} onDone={onKeyed} />
          <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={onOpen} title="Open full view" data-testid={`hold-open-${id}`}><Eye className="w-4 h-4" /></Button>
        </div>
      </TableCell>
    </TableRow>
  );
};

/** Direct Sales invoices the auto-push held back — fix the cause, then Re-check & push (or Re-check all). */
export const EwbHoldsPanel = ({ onOpen, onCount, onChanged, tick }) => {
  const [rows, setRows] = useState(null);
  const [km, setKm] = useState({});
  const [busy, setBusy] = useState('');
  const [runId, setRunId] = useState(null);
  const load = useCallback(() => holdsApi.list().then((d) => { setRows(d.holds); onCount?.(d.count); })
    .catch((e) => toast.error(errMsg(e, 'Failed to load'))), [onCount]);
  useEffect(() => { load(); }, [load, tick]);
  const changed = () => { load(); onChanged?.(); };
  const recheck = (h, customerId) => {
    setBusy(hkey(h));
    holdsApi.recheck({ doc_no: h.doc_no, doc_type: h.doc_type || 'customer_invoice', ...(customerId ? { customer_id: customerId } : {}),
      ...(h.needs_distance && km[hkey(h)] ? { distance_km: Number(km[hkey(h)]) } : {}) })
      .then(resultToast).catch((e) => toast.error(errMsg(e, 'Re-check failed'))).finally(() => { setBusy(''); changed(); });
  };
  const recheckAll = () => holdsApi.recheckAll().then((r) => setRunId(r.id)).catch((e) => toast.error(errMsg(e, 'Re-check failed')));
  return (
    <div className="space-y-3" data-testid="ewb-holds-panel">
      <div className="space-y-3 rounded-xl bg-white p-3 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="max-w-xl text-sm text-gray-600">Uploaded Direct Sales invoices and EBO Transfer Outs become e-way bills on their own. The ones below were held — fix what
            the remark says (Transport Master, FG Pick Slips, Customer / Rates Master), then press <b>Re-check &amp; push</b>. Made the e-way bill
            on the GST portal yourself? Press <b>Type in EWB No</b> and enter its number, generated date and validity.</div>
          <Button size="sm" variant="outline" onClick={recheckAll} disabled={!rows?.length} data-testid="holds-recheck-all-btn"><RefreshCw className="w-4 h-4 mr-1" />Re-check all</Button>
        </div>
        <div className="grid gap-3 md:grid-cols-3 items-start" data-testid="holds-upload-row"><DocUploadButton kind="customer_invoice" onDone={changed} /><DocUploadButton kind="outside_invoice" onDone={changed} />
          <DocUploadButton kind="transfer_out" onDone={changed} /></div>
      </div>
      <AutoPushSummary runId={runId} onDone={changed} />
      {!rows ? <Loader2 className="w-6 h-6 mx-auto my-10 animate-spin text-amber-600" />
        : !rows.length ? <div className="rounded-xl bg-white py-14 text-center text-gray-500 shadow-sm" data-testid="holds-empty">Nothing needs attention — every uploaded invoice / Transfer Out has its e-way bill</div>
          : (
            <div className="overflow-x-auto rounded-xl bg-white shadow-sm">
              <Table data-testid="holds-table">
                <TableHeader><TableRow>{HEADS.map((x) => <TableHead key={x}>{x}</TableHead>)}</TableRow></TableHeader>
                <TableBody>
                  {rows.map((h) => (
                    <HoldRow key={hkey(h)} h={h} busy={busy === hkey(h)} km={km[hkey(h)] || ''} setKm={(v) => setKm({ ...km, [hkey(h)]: v })}
                      onRecheck={() => recheck(h)} onPick={(cid) => recheck(h, cid)} onOpen={() => onOpen(h)} onKeyed={changed} />
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
    </div>
  );
};

export default EwbHoldsPanel;
